/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

// List of features currently being A/B tested.
export const TEST_FEATURES = {
	WELCOME_MESSAGE: 'WELCOME_MESSAGE_TEST',
};
// Mapping of feature to seed value. We should generally aim to used a unique seed value
// for each concurrent test to ensure that the hash is different. See the generateSeed function
// at the bottom of this file. This function should be used in development only and for the one-time generation of the seed value.
// For example: for the WELCOME_MESSAGE_TEST, the input to the generateSeed function will be 'PersonalMessage-20180730' and the resulting
// output is the seed value of 1950.

export const TEST_SEED_MAP = {
	UNIT_TEST_SEED: 999,
	DEFAULT: 1,
	ACTIVATION_TEST_SEED: 1441,
	OPUS_TEST_SEED: 1126,
	SSO_TEST_SEED: 1044,
	CCHOME_REDIRECT_TEST: 1323,
	WELCOME_MESSAGE_TEST: 1950,
	FIRST_TEST_RUN: 1111,
	SECOND_TEST_RUN: 1001, // test code 20181214-Search
	THIRD_TEST_RUN: 1574, // test code 20181214_QuickStart
	'20190125_SUGESSTIONSUI': 1836, // test code 20190125_SuggestionsUI
	CCWEB_REVAMP_SEED: 1294,
	SURVEY_REMOVAL_TEST: 1538, // https://jira.corp.adobe.com/browse/CCH-6400
	THROUGHLINE_TEST_SEED: 1640,
	DESKTOP_APP_CTA_TEST: 1584, // https://jira.corp.adobe.com/browse/CCH-7329
	THROUGHLINE_PHOTOSHOP_TEST_SEED: 2443,
	THROUGHLINE_V3_TEST_SEED: 1872,
	CATEGORY_REVAMP_SEED: 1544,
	BANNER_ON_MOBILE: 1682,
};

export const TEST_CODE_MAP = {
	WELCOME_MESSAGE_TEST: 'PersonalMessage-20180730',
	ACTIVATION_TEST_SEED: '20181207-Popup-Alert',
	CCHOME_REDIRECT_TEST: '20181207-THOR-Detect',
	BANNER_ON_MOBILE: '2021-1-BannerOnMobile',
};

/**
 * Use the Murmur3 algorithm to hash user id into range from 0 to 99
 * @param {string} test the name of the test that this function will map to a seed value for the hash.
 * @returns number
 */
export function murmurHashUserId(userId: string, test: string) {
	let seed = TEST_SEED_MAP[test];

	// If no test was passed or there is no test to seed mapping, use the default
	// seed.
	if (!seed) {
		seed = TEST_SEED_MAP.DEFAULT;
	}

	let hashKey = 0;
	if (userId) {
		// Extract the guid portion from the userid
		// Enterprise Ids may not have the AdobeId portion of the userId and may have a different domain specifier
		const guid = userId.match(/^.*(?=(@))/);

		if (guid && guid[0]) {
			// generate the hash using the Murmur3 algorithm
			const hash = murmurhash332gc(guid[0], seed);

			// hash % 100 is our hash key
			const hashMod = 100;
			hashKey = hash % hashMod;
		}
	}
	return hashKey;
}

// Murmur3 JS implementation from https://github.com/garycourt/murmurhash-js
// MIT License

/**
 *
 * @param {string} key ASCII Only
 * @param {number} seed Positive integer only
 * @returns {number} 32-bit positive integer hash
 */

/* istanbul ignore next */
function murmurhash332gc(key: any, seed: any) {
	let h1;
	let h1b;
	let k1;
	let i;

	const remainder = key.length & 3; // key.length % 4
	const bytes = key.length - remainder;
	h1 = seed;
	const c1 = 0xcc9e2d51;
	const c2 = 0x1b873593;
	i = 0;

	while (i < bytes) {
		k1
			= (key.charCodeAt(i) & 0xff)
			| ((key.charCodeAt(++i) & 0xff) << 8)
			| ((key.charCodeAt(++i) & 0xff) << 16)
			| ((key.charCodeAt(++i) & 0xff) << 24);
		++i;

		k1 = ((k1 & 0xffff) * c1 + ((((k1 >>> 16) * c1) & 0xffff) << 16)) & 0xffffffff;
		k1 = (k1 << 15) | (k1 >>> 17);
		k1 = ((k1 & 0xffff) * c2 + ((((k1 >>> 16) * c2) & 0xffff) << 16)) & 0xffffffff;

		h1 ^= k1;
		h1 = (h1 << 13) | (h1 >>> 19);
		h1b = ((h1 & 0xffff) * 5 + ((((h1 >>> 16) * 5) & 0xffff) << 16)) & 0xffffffff;
		h1 = (h1b & 0xffff) + 0x6b64 + ((((h1b >>> 16) + 0xe654) & 0xffff) << 16);
	}

	k1 = 0;

	switch (remainder) {
		case 3:
			k1 ^= (key.charCodeAt(i + 2) & 0xff) << 16;
			// falls through
		case 2:
			k1 ^= (key.charCodeAt(i + 1) & 0xff) << 8;
			// falls through
		case 1:
			k1 ^= key.charCodeAt(i) & 0xff;
			k1 = ((k1 & 0xffff) * c1 + ((((k1 >>> 16) * c1) & 0xffff) << 16)) & 0xffffffff;
			k1 = (k1 << 15) | (k1 >>> 17);
			k1 = ((k1 & 0xffff) * c2 + ((((k1 >>> 16) * c2) & 0xffff) << 16)) & 0xffffffff;
			h1 ^= k1;
			// falls through
	}

	h1 ^= key.length;

	h1 ^= h1 >>> 16;
	h1 = ((h1 & 0xffff) * 0x85ebca6b + ((((h1 >>> 16) * 0x85ebca6b) & 0xffff) << 16)) & 0xffffffff;
	h1 ^= h1 >>> 13;
	h1 = ((h1 & 0xffff) * 0xc2b2ae35 + ((((h1 >>> 16) * 0xc2b2ae35) & 0xffff) << 16)) & 0xffffffff;
	h1 ^= h1 >>> 16;

	return h1 >>> 0;
}

/**
 * DEV USE ONLY: This function generates a seed value given a particular test code using the logic provided by Haresh.
 * This should only be used for development and for generating a seed value for a new test.
 * @param {string} testCode
 * @returns {number} seed the seed value for the given test code.
 */

/*
function generateSeed(testCode) {

	// Split the parts of test code.
	const parts = testCode.split('-');

	let totalCount = 0;

	// Add up the ascii codes for each character in the test code.
	// This will be value of the seed for the test.
	parts.forEach((part) => {
		const partsSplit = part.toString().split('');
		partsSplit.forEach(char => totalCount += char.charCodeAt(0));
	});

	return totalCount;

}
*/
